import React, { useState, useCallback } from 'react'
import { Box, Flex, Text, Badge, IconButton, useColorModeValue } from '@chakra-ui/react'
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons'
import { Droppable, Draggable } from 'react-beautiful-dnd'
import { Card } from './Card'

// Componente principal do Kanban Board
export const KanbanBoard = React.memo(({ columns, columnData, setPriorityColor, updatePriorityChamado, listPriority, priority }) => {
  const [collapsedColumns, setCollapsedColumns] = useState({
    FRONT: false,
    BACK: false,
    BANCO: false,
    'SEM DEFINICAO': false,
  })

  const columnBackground = useColorModeValue('gray.50', 'gray.700')
  const columnHeaderBg = useColorModeValue('gray.100', 'gray.600')

  const toggleColumnCollapse = useCallback((column) => {
    setCollapsedColumns((prev) => ({
      ...prev,
      [column]: !prev[column],
    }))
  }, [])

  return (
    <Flex
      overflow="auto"
      p={4}
      gap={6} // Aumentando o gap entre colunas
      flexDirection={{ base: 'column', lg: 'row' }}
    >
      {columns.map((column) => (
        <KanbanColumn
          key={column.id}
          column={column}
          isCollapsed={collapsedColumns[column.id]}
          toggleCollapse={toggleColumnCollapse}
          columnBackground={columnBackground}
          columnHeaderBg={columnHeaderBg}
          cards={columnData[column.id] || []}
          setPriorityColor={setPriorityColor}
          updatePriorityChamado={updatePriorityChamado}
          listPriority={listPriority}
          priority={priority}
        />
      ))}
    </Flex>
  )
})

// Componente de coluna do Kanban
const KanbanColumn = React.memo(
  ({
    column,
    isCollapsed,
    toggleCollapse,
    columnBackground,
    columnHeaderBg,
    cards,
    setPriorityColor,
    updatePriorityChamado,
    listPriority,
    priority,
  }) => {
    return (
      <Box
        width={{ base: '100%', lg: '25%' }}
        minWidth={{ base: '100%', lg: '300px' }}
        maxWidth={{ base: '100%', lg: '400px' }}
        height={{ base: 'auto', lg: 'calc(100vh - 220px)' }}
        borderRadius="md"
        bg={columnBackground}
        boxShadow="md" // Sombra mais visível
        mb={{ base: 4, lg: 0 }}
        flex="1"
        display="flex"
        flexDirection="column"
        overflow="hidden" // Isso evita que o conteúdo ultrapasse os limites da coluna
      >
        <Flex
          alignItems="center"
          justifyContent="space-between"
          bg={columnHeaderBg}
          p={3}
          borderTopRadius="md"
          borderBottomWidth={isCollapsed ? '0' : '1px'}
          borderBottomColor="gray.200"
          boxShadow="sm"
        >
          <Flex alignItems="center">
            <Box w="3" h="3" borderRadius="full" bg={column.color} mr={2} />
            <Text fontWeight="bold" fontSize="lg">
              {column.title}
            </Text>
            <Badge ml={2} colorScheme={column.id === 'SEM DEFINICAO' ? 'gray' : 'blue'}>
              {cards.length || 0}
            </Badge>
          </Flex>

          <IconButton
            icon={isCollapsed ? <ViewIcon /> : <ViewOffIcon />}
            size="sm"
            variant="ghost"
            onClick={() => toggleCollapse(column.id)}
            aria-label={isCollapsed ? 'Expandir' : 'Recolher'}
          />
        </Flex>

        {!isCollapsed && (
          <Droppable droppableId={column.id}>
            {(provided, snapshot) => (
              <Box
                ref={provided.innerRef}
                {...provided.droppableProps}
                flex="1"
                overflowY="auto"
                p={3}
                bg={snapshot.isDraggingOver ? 'gray.100' : 'transparent'}
                transition="background-color 0.2s ease"
                sx={{
                  // Estilizando a scrollbar para melhor UX
                  '&::-webkit-scrollbar': {
                    width: '8px',
                  },
                  '&::-webkit-scrollbar-track': {
                    width: '10px',
                    background: 'transparent',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: 'gray.300',
                    borderRadius: '24px',
                  },
                }}
              >
                <Flex
                  direction="column"
                  gap={4} // Espaçamento adequado entre cards
                >
                  {cards.map((item, index) => (
                    <Draggable key={item.numero_chamado} draggableId={item.numero_chamado.toString()} index={index}>
                      {(provided, snapshot) => (
                        <Box
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          transform={provided.draggableProps.style.transform}
                          transition={snapshot.isDragging ? 'none' : 'transform 0.2s ease-in-out'}
                          zIndex={snapshot.isDragging ? 100 : 1}
                        >
                          <Card
                            item={item}
                            setPriorityColor={setPriorityColor}
                            updatePriorityChamado={updatePriorityChamado}
                            listPriority={listPriority}
                            priority={priority}
                            isDragging={snapshot.isDragging}
                          />
                        </Box>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </Flex>
              </Box>
            )}
          </Droppable>
        )}
      </Box>
    )
  }
)
